import { ReactElement } from 'react';
import { Stringable } from '../../../services/helpers/Stringable';
import useInitialData from '../../../hooks/global/useInitialData';
import { ICompanyImpressum } from '../../../services/exports/Interfaces';
import { useGetFranchiseImpressumsQuery } from '../../../store/api-slices/CompaniesApiSlice';
import ImpressumResource from '../../../services/resources/ImpressumResource';
import PageNotFound from '../../PageNotFound';

const Impressum = (): ReactElement => {
  const { data } = useInitialData();
  const { company, franchise } = data;
  const logoUrl = company?.logo_url ?? franchise?.logo_url;

  // Always show every company in the franchise, sourced from the dedicated endpoint.
  // Values are resolved server-side (override → business_profile fallback).
  const { data: franchiseImpressums } = useGetFranchiseImpressumsQuery();

  // A self-serve restaurant's own text, exactly as pasted: nothing generated, no boilerplate.
  if (ImpressumResource.usesOwnImpressum(company)) {
    // The page matches the footer, which links it only once this restaurant pasted an
    // imprint. Without one there is no page — another restaurant's text from the
    // franchise list is not this restaurant's Impressum.
    if (!ImpressumResource.hasOwnImpressum(company)) {
      return <PageNotFound />;
    }

    const ownImpressums = ImpressumResource.ownImpressums(company, franchiseImpressums);

    return (
      <div className="flex flex-col w-screen lg:py-xl py-lg lg:px-xl px-md">
        {ownImpressums.map((item) => (
          <div
            key={item.company_id}
            className="border-b border-brand-border dark:border-brand-border pb-md mb-xl last:border-b-0"
            data-test="own-impressum"
          >
            {ownImpressums.length > 1 && item.company_name ? <h4 className="mb-md">{item.company_name}</h4> : null}
            <p className="whitespace-pre-line break-words">{item.content}</p>
          </div>
        ))}
      </div>
    );
  }

  // Defensive fallback to the single Redux company's resolved impressum (and its
  // business_profile) so the page is never blank — e.g. while the list loads, if it
  // errors, or before the backend that serves the endpoint is deployed.
  const impressum = company?.impressum;
  const businessProfile = company?.business_profile;

  const items: ICompanyImpressum[] =
    franchiseImpressums && franchiseImpressums.length > 0
      ? franchiseImpressums
      : [
          {
            company_id: company?.id ?? 0,
            company_name: company?.name ?? '',
            slug: company?.slug ?? '',
            legal_name: impressum?.legal_name ?? businessProfile?.legal_name ?? null,
            owner_first_name: impressum?.owner_first_name ?? businessProfile?.owner_first_name ?? null,
            owner_last_name: impressum?.owner_last_name ?? businessProfile?.owner_last_name ?? null,
            owner_email: impressum?.owner_email ?? businessProfile?.owner_email ?? null,
            owner_phone_number: impressum?.owner_phone_number ?? businessProfile?.owner_phone_number ?? null,
            tax_id: impressum?.tax_id ?? businessProfile?.tax_id ?? null,
            full_address:
              impressum?.full_address ??
              businessProfile?.full_invoice_address ??
              businessProfile?.full_legal_address ??
              null,
          },
        ];

  const multiple = items.length > 1;

  const renderRow = (key: any, value: any): ReactElement | null => {
    return value ? (
      <div className="flex mb-sm items-center">
        <div className="mr-sm">
          <h6>{key}:</h6>
        </div>
        <p>{value}</p>
      </div>
    ) : null;
  };

  const renderImpressumItem = (item: ICompanyImpressum, index: number): ReactElement => {
    const ownerName = `${item.owner_first_name ?? ''} ${item.owner_last_name ?? ''}`.trim();

    return (
      <div key={item.company_id || index} className="border-b border-brand-border dark:border-brand-border pb-md mb-xl">
        {index === 0 && logoUrl ? (
          <a href={new Stringable(logoUrl).start('https://').toString()} target="_blank" rel="noopener noreferrer">
            <img src={logoUrl} alt="logo" className="h-[100px] object-contain mb-sm" />
          </a>
        ) : null}
        {multiple && item.company_name ? <h4 className="mb-md">{item.company_name}</h4> : null}
        {renderRow('Unternehmen', item.legal_name)}
        {renderRow('Inhaber', ownerName)}
        {renderRow('Adresse', item.full_address)}
        {renderRow('Telefon', item.owner_phone_number)}
        {renderRow('E-Mail', item.owner_email)}
        {renderRow('Steuer-ID', item.tax_id)}
        {!multiple ? renderRow('Website', window.location.origin) : null}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-screen lg:py-xl py-lg lg:px-xl px-md">
      <div className="flex flex-col">
        {items.map((item, index) => renderImpressumItem(item, index))}
        {multiple ? (
          <div className="border-b border-brand-border dark:border-brand-border pb-md mb-xl">
            {renderRow('Website', window.location.origin)}
          </div>
        ) : null}
      </div>
      <p className="mb-md">
        Wir sind zur Teilnahme an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle weder
        verpflichtet noch bereit.
      </p>
      <p className="mb-md">
        <em>Alle Angaben gemäß § 5 TMG</em>
      </p>
      <h2 className="mb-md">Haftungsausschluss</h2>
      <h3 className="mb-md">Haftung für Inhalte</h3>
      <p className="mb-md">
        Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen
        Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet,
        übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine
        rechtswidrige Tätigkeit hinweisen.
      </p>
      <p className="mb-md">
        Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben
        hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten
        Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte
        umgehend entfernen.
      </p>
      <h3 className="mb-md">Haftung für Links</h3>
      <p className="mb-md">
        Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb
        können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist
        stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum
        Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der
        Verlinkung nicht erkennbar.
      </p>
      <p className="mb-md">
        Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer
        Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend
        entfernen.
      </p>
      <h3 className="mb-md">Urheberrecht</h3>
      <p className="mb-md">
        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen
        Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen
        des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und
        Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.
      </p>
      <p className="mb-md">
        Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter
        beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine
        Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von
        Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
      </p>
    </div>
  );
};

export default Impressum;
